import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Lead, LeadStatus } from '@embrace-returns/shared';
import { api } from '../api/client';
import { useAuth, isManager } from '../auth/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { PassOnPanel } from '../components/PassOnPanel';
import { copyText, fmtDate, gbp, leadsToText } from '../leadText';

const TABS: { key: LeadStatus; label: string }[] = [
  { key: 'New', label: 'To pass on' },
  { key: 'Closed', label: 'Passed on' },
];

export default function Inbox() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState<LeadStatus>('New');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [passOnLead, setPassOnLead] = useState<Lead | null>(null);

  const load = useCallback(async (status: LeadStatus) => {
    try {
      const res = await api.leads.list(status);
      setLeads(res.leads ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setSelected(new Set());
    load(tab);
    const id = setInterval(() => load(tab), 30000);
    return () => clearInterval(id);
  }, [tab, load]);

  const toggle = (leadId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(leadId) ? next.delete(leadId) : next.add(leadId);
      return next;
    });

  const allSelected = leads.length > 0 && selected.size === leads.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(leads.map((l) => l.leadId)));

  const selectedLeads = useMemo(
    () => leads.filter((l) => selected.has(l.leadId)),
    [leads, selected],
  );

  const copySelected = async () => {
    if (await copyText(leadsToText(selectedLeads))) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const scopeLabel = isManager(user) ? 'All returned leads' : 'Your returned leads';

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-[52px] flex-shrink-0 items-center justify-between border-b border-hair bg-white px-6">
        <h1 className="text-[15px] font-semibold text-ink">Inbox</h1>
        <span className="text-[12px] text-muted">{scopeLabel}</span>
      </header>

      <div className="flex flex-shrink-0 items-center gap-2 border-b border-hair bg-white px-6 py-3">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-colors ${
                active ? 'bg-navy text-white' : 'bg-gray-100 text-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-3">
          {selected.size > 0 && (
            <button
              onClick={copySelected}
              className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3 py-1.5 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <rect x="9" y="9" width="13" height="13" rx="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {copied ? 'Copied' : `Copy selected (${selected.size})`}
            </button>
          )}
          <span className="text-[12px] text-muted">
            {leads.length} {leads.length === 1 ? 'lead' : 'leads'}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="py-20 text-center text-[13px] text-muted">Loading leads…</div>
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-[13px] text-muted">
            {tab === 'New'
              ? 'Nothing waiting. New leads from Simply Lending will land here.'
              : 'Nothing passed on yet.'}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-hair bg-[#f8fafc]">
                  <th className="w-10 px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="cursor-pointer accent-navy"
                    />
                  </th>
                  {[
                    'Customer',
                    ...(isManager(user) ? ['Introducer'] : []),
                    'Mortgage',
                    'Lender',
                    'Amount',
                    'Received',
                    'Status',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2.5 text-left text-[10.5px] font-semibold uppercase tracking-wide text-muted"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr
                    key={lead.leadId}
                    onClick={() => navigate(`/leads/${lead.leadId}`)}
                    className={`cursor-pointer border-b border-gray-100 transition-colors last:border-0 hover:bg-gray-50 ${
                      selected.has(lead.leadId) ? 'bg-emerald-50/40' : ''
                    }`}
                  >
                    <td className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(lead.leadId)}
                        onChange={() => toggle(lead.leadId)}
                        className="cursor-pointer accent-navy"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[13px] font-semibold text-ink">{lead.customerName}</div>
                    </td>
                    {isManager(user) && (
                      <td className="px-4 py-3">
                        <div className="text-[12.5px] font-medium text-ink">
                          {lead.embraceAdviserName || lead.embraceAdviserEmail}
                        </div>
                        {lead.embraceAdviserName && (
                          <div className="mt-0.5 text-[11px] text-muted">
                            {lead.embraceAdviserEmail}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-[12.5px] text-ink">
                      {[lead.mortgageType, lead.repaymentType].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12.5px] text-muted">{lead.mortgageProvider ?? '—'}</td>
                    <td className="px-4 py-3 text-[12.5px] text-ink">{gbp(lead.mortgageAmount)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-[12px] text-muted">
                      {fmtDate(lead.receivedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={lead.status} />
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {lead.status === 'New' && (
                        <button
                          onClick={() => setPassOnLead(lead)}
                          className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90"
                        >
                          Pass on
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {passOnLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setPassOnLead(null)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-hair bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[14px] font-bold text-ink">
              Pass on lead — {passOnLead.customerName}
            </h2>
            <div className="mt-3">
              <PassOnPanel
                leadId={passOnLead.leadId}
                ownerEmail={passOnLead.embraceAdviserEmail}
                onDone={() => {
                  setPassOnLead(null);
                  void load(tab);
                }}
                onCancel={() => setPassOnLead(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
